import React, { useEffect, useState } from 'react';
import './styles/action-list-header.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSeedling,
  faCircleCheck,
  faInfoCircle,
  faSyncAlt,
  faExclamationTriangle,
  faChevronDown,
  faChevronUp,
} from '@fortawesome/free-solid-svg-icons';
import { SdgHeader } from '../types/Sdg';
import { Link } from 'react-router-dom';
import { pageNames } from 'config/pageNames';

interface ActionListHeaderProps {
  sdg: number;
  eventId?: string;
  showDescription?: boolean; // kept for compatibility
  changeSdg: () => void;
  onShowMore: () => void;    // kept, but optional now
  expand?: boolean;          // no longer needed for UI
  sdgHeader: SdgHeader | null;
  canChange: boolean;
}

const ActionListHeader: React.FC<ActionListHeaderProps> = (props): JSX.Element => {
  const { sdg, sdgHeader, changeSdg, eventId, canChange } = props;
  const allowWorkOnTasks = false;

  // NEW: controls visibility of indicators + benefits
  const [showDetails, setShowDetails] = useState<boolean>(false);

  useEffect(() => {
    // Reset details when SDG header changes
    setShowDetails(false);
  }, [sdgHeader]);

  const toggleDetails = () => setShowDetails((prev) => !prev);

  return (
    <section className="action-header-card">
      {/* No SDG header yet → show guidance to run materiality exercise */}
      {!sdgHeader && (
        <div className="action-header-warning">
          <div className="action-header-warning-icon">
            <FontAwesomeIcon
              icon={faExclamationTriangle}
              className="text-warning"
              size="lg"
            />
          </div>
          <div className="action-header-warning-content">
            <p className="action-header-warning-title">
              Get more relevant actions for your event
            </p>
            <p className="action-header-warning-body">
              We recommend doing your materiality exercise to get sustainability
              actions aligned with your event’s real priorities.
            </p>
            <Link to={pageNames.SDG_SELECTOR} className="action-header-link">
              Go to Materiality Exercise
            </Link>
          </div>
        </div>
      )}

      {/* SDG header exists → show full details */}
      {sdgHeader && (
        <>
          <header className="action-header-top w-100">
            <div>
              <p className="action-header-kicker">Sustainability focus</p>
              <h2 className="action-header-title">
                {sdgHeader.title}{' '}
                <span className="action-header-sdg-pill">
                  <FontAwesomeIcon icon={faSeedling} className="me-1" />
                  {sdgHeader.shortTitle}
                </span>
              </h2>
              <p className="action-header-subtitle">
                This platform will help you generate a list of actions to address
                the goal above and move your event closer to its sustainability
                ambitions.
              </p>
            </div>

            <div className="action-header-actions">
              {canChange && (
                <button
                  type="button"
                  className="billing-button billing-button--outline action-header-change-btn"
                  onClick={changeSdg}
                >
                  <FontAwesomeIcon icon={faSyncAlt} className="me-2" />
                  Change focus…
                </button>
              )}

              <button
                type="button"
                className="billing-button billing-button--outline action-header-toggle-btn"
                onClick={toggleDetails}
                aria-expanded={showDetails}
              >
                <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                {showDetails ? 'Hide details' : 'Goal details'}
                <FontAwesomeIcon
                  icon={showDetails ? faChevronUp : faChevronDown}
                  className="ms-2"
                />
              </button>
            </div>
          </header>

          <div className="action-header-body">
            {/* Goal description (always shown) */}
            <div className="action-header-section">
              <div className="action-header-section-label">
                <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                <span>About this goal</span>
              </div>
              <p className="action-header-goal">
                {sdgHeader.goal}
              </p>
            </div>

            {/* Collapsible details: Indicators + Benefits */}
            {showDetails && (
              <div className="action-header-details">
                {/* Indicators list */}
                <div className="action-header-section">
                  <div className="action-header-section-label">
                    <FontAwesomeIcon icon={faSeedling} className="me-2" />
                    <span>Key indicators you could influence</span>
                  </div>
                  <div className="action-header-indicators">
                    {sdgHeader.indicators.map((indicator) => (
                      <div
                        className="action-header-indicator-row"
                        key={indicator.index}
                      >
                        <div className="action-header-indicator-content">
                          <span className="action-header-indicator-icon">
                            <FontAwesomeIcon icon={faSeedling} />
                          </span>
                          <span className="ms-2 action-header-indicator-index">
                            {indicator.index}
                          </span>
                          <span className="ms-2 action-header-indicator-title">
                            {indicator.title}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Benefits block */}
                <div className="action-header-benefits">
                  <p className="action-header-section-label">
                    How working on this goal can help
                  </p>
                  <ul className="action-header-benefits-list">
                    <li>
                      <FontAwesomeIcon icon={faCircleCheck} className="me-2" />
                      <span>Advance the Paris Agreement.</span>
                    </li>
                    <li>
                      <FontAwesomeIcon icon={faCircleCheck} className="me-2" />
                      <span>Support Race to Zero commitments and climate leadership.</span>
                    </li>
                  </ul>
                </div>
              </div>
            )}

            {/* Optional CTA for tasks (kept, gated by allowWorkOnTasks) */}
            {eventId && allowWorkOnTasks && (
              <div className="action-header-footer">
                <Link
                  to={`${pageNames.ACTIONS_BOARD}?eventId=${eventId}`}
                  className="billing-button billing-button--primary"
                >
                  <FontAwesomeIcon icon={faSeedling} className="me-2" />
                  Work on the tasks for this SDG
                </Link>
              </div>
            )}
          </div>
        </>
      )}
    </section>
  );
};

export default ActionListHeader;
